import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SlideInRightModalComponent } from '@app/shared/ui/slide-in-right-modal/slide-in-right-modal.component';
import { AccountService } from '@app/core/services/client/account.service';
import { ProfieDetails } from '@app/core/models/client/account/account-details.model';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { AuthSelectors } from '@app/auth/store/auth.selectors';
import { environment } from '@app/core/environments/environment';

@Component({
  selector: 'app-edit-profile-picture',
  standalone: true,
  imports: [
    CommonModule,
    SlideInRightModalComponent
  ],
  templateUrl: './edit-profile-picture.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditProfilePictureComponent {
  @Input() kycDetails?: ProfieDetails;
  @Output() photoChanged = new EventEmitter<string>();
  @Output() action = new EventEmitter<void>();

  private accountService = inject(AccountService);
  private cdr = inject(ChangeDetectorRef);
  private store = inject(Store);

  profileID = this.store.selectSnapshot(AuthSelectors.getProfileID);

  returnPathUrl!: string;
  previewUrl: string | null = null;
  selectedFile: File | null = null;
  selectedFileName = '';
  isUploading = false;

  private readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  private readonly ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

  closeModal(): void {
    this.action.emit();
  }

  /* -------------------- FILE SELECTION (PREVIEW ONLY) -------------------- */
  onPhotoSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    if (!this.ALLOWED_TYPES.includes(file.type)) {
      alert('Please select a valid image file (JPG or PNG)');
      return;
    }

    if (file.size > this.MAX_FILE_SIZE) {
      alert('File size must be less than 5MB');
      return;
    }

    this.selectedFile = file;
    this.selectedFileName = file.name;

    const reader = new FileReader();
    reader.onload = () => {
      this.previewUrl = reader.result as string;
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(file);
  }

  /* -------------------- CONFIRM UPLOAD -------------------- */
  confirmUpload(): void {
    if (!this.selectedFile || !this.profileID) return;

    const formData = new FormData();
    formData.append('file', this.selectedFile);
    formData.append('user_id', this.profileID);

    this.isUploading = true;
    this.cdr.markForCheck();

    this.accountService.uploadProfilePhoto(formData).subscribe({
      next: (event: HttpEvent<any>) => {
        if (event.type === HttpEventType.Response) {
          this.isUploading = false;

          const photoPath = event.body?.data;
          const newPhotoUrl = photoPath
            ? `${environment.FILE_BASE_URL}${photoPath}`
            : this.previewUrl!;

          this.photoChanged.emit(newPhotoUrl);
          this.cdr.markForCheck();
          this.closeModal();
        }
      },
      error: (error) => {
        this.isUploading = false;
        this.cdr.markForCheck();

        console.error('Profile Picture Upload failed:', error);
      },
    });
  }
}
